import { FormEvent, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined'

import { LoginResponse, ResponseError } from '../../clients'
import { AppAlert } from '../../components/AppAlerts/AppAlerts'
import { addAlert } from '../../redux/features/alerts/alertsSlice'
import { authenticate } from '../../redux/features/auth/authSlice'
import { useAppDispatch } from '../../redux/hooks'
import { getAuthService } from '../../services/authService'
import { RecaptchaService } from '../../services/recaptchaService'
import { getErrorModel } from '../../utils/getErrorModel'

enum LoginStep {
    Default,
    MFA
}

export const SignIn = () => {

    const dispatch = useAppDispatch()
    const [email, setEmail] = useState<string>('')
    const [emailError, setEmailError] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [passwordError, setPasswordError] = useState<string>('')
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [step, setStep] = useState<LoginStep>(LoginStep.Default)
    const [mfaCode, setMfaCode] = useState<string>('')
    const [mfaError, setMfaError] = useState<string>('')
    const [mfaDestination, setMfaDestination] = useState<string>('')
    const [rememberMe, setRememberMe] = useState<boolean>(false)

    const cognitoSub = useRef<string>('')
    const cognitoSession = useRef<string>('')
    const mfaChallengeType = useRef<string | null>(null)

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const recaptchaService = new RecaptchaService()
        const recaptchaToken = await recaptchaService.getToken()
            .catch(() => '')
        if (emailError || submitting) {
            return
        }
        setSubmitting(true)
        try {
            const authService = await getAuthService()
            const res = await authService.passwordLogin(email, password, rememberMe, recaptchaToken)
            if (res.challengeName === 'SMS_MFA') {
                setStep(LoginStep.MFA)
                setMfaDestination(res.challengeParameters?.cODEDELIVERYDESTINATION ?? '')
                mfaChallengeType.current = res.challengeName
                cognitoSub.current = res.username ?? ''
                cognitoSession.current = res.session ?? ''
            }
            if (res.authenticationResult) {
                dispatch(authenticate(res.authenticationResult))
            }
        } catch (err) {
            const error = await getErrorModel(err)
            if (!error) {
                const alert = new AppAlert('error', 'Something went wrong, try again later', true)
                return dispatch(addAlert(alert.toJSON()))
            }
            if (error.errors.some(x => x.message.includes('invalid username or password'))) {
                setPasswordError('Incorrect password')
                return setSubmitting(false)
            }
        }
        setSubmitting(false)
    }

    const handleMFASubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!mfaCode || !mfaChallengeType.current || submitting) {
            return
        }
        setMfaError('')
        try {
            const authService = await getAuthService()
            let res: LoginResponse | null = null
            if (mfaChallengeType.current === 'SMS_MFA') {
                res = await authService.respondToSMSToken(cognitoSub.current, mfaCode, cognitoSession.current)
            } else if (mfaChallengeType.current === 'SOFTWARE_TOKEN_MFA') {
                res = await authService.respondToSoftwareToken(cognitoSub.current, mfaCode, cognitoSession.current)
            }
            if (res?.authenticationResult) {
                dispatch(authenticate(res.authenticationResult))
            }
        } catch (err) {
            const error = err as ResponseError
            const res = error.response
            const text = await res.text().catch(() => null)
            if (text === 'Invalid session') {
                setStep(LoginStep.Default)
                const alert = new AppAlert('error', 'Session expired. Please try again.', true)
                dispatch(addAlert(alert.toJSON()))
            }
            if (text === 'Invalid code') {
                setMfaError('Invalid Code')
            }
        }
    }

    const handleEmailFocus = () => {
        setEmailError('')
    }

    const handlePasswordFocus = () => {
        setPasswordError('')
    }

    const handleMfaFocus = () => {
        setMfaError('')
    }

    const handleEmailBlur = (): string => {
        if (!email) {
            return ''
        }
        const emailRegex = new RegExp(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g)
        if (!email.match(emailRegex)?.length) {
            const err = 'Invalid email address'
            setEmailError(err)
            return err
        }
        return ''
    }

    const handleGoogleSignIn = () => {
        const idpIdentifierUrl = `https://${import.meta.env.VITE_COGNITO_AUTH_DOMAIN}/oauth2/authorize?response_type=code&client_id=${import.meta.env.VITE_COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(`${window.location.origin}/auth/login-callback`)}&identity_provider=Google`
        window.location.href = idpIdentifierUrl
    }

    // const handleFacebookSignIn = () => {
    //   const idpIdentifierUrl = `${import.meta.env.VITE_COGNITO_AUTH_DOMAIN}/oauth2/authorize?response_type=code&client_id=${import.meta.env.VITE_COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(`${window.location.origin}/auth/login-callback`)}&identity_provider=Facebook`
    //   window.location.href = idpIdentifierUrl
    // }

    if (step === LoginStep.MFA) {
        return (
            <Box sx={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
                <Container component='div' maxWidth='sm'>
                    <Paper elevation={3} sx={{ borderRadius: 2 }}>
                        <Card>
                            <CardContent sx={{ p: 4 }}>
                                <Stack spacing={3} alignItems='center'>
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: 2,
                                            bgcolor: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <SecurityOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                            Multi-Factor Authentication
                                        </Typography>
                                        <Typography variant='body2' color='text.secondary'>
                                            Enter the code sent to {mfaDestination}
                                        </Typography>
                                    </Box>
                                    <Box component='form' onSubmit={handleMFASubmit} sx={{ width: '100%' }}>
                                        <Stack spacing={3}>
                                            <TextField
                                                required
                                                fullWidth
                                                value={mfaCode}
                                                error={!!mfaError}
                                                helperText={mfaError}
                                                onFocus={handleMfaFocus}
                                                onChange={e => setMfaCode(e.target.value)}
                                                label='Verification Code'
                                                autoFocus
                                            />
                                            <Button
                                                disabled={submitting || !mfaCode}
                                                type='submit'
                                                fullWidth
                                                variant="contained"
                                                size="large"
                                                sx={{ mt: 2 }}
                                            >
                                                Verify
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Paper>
                </Container>
            </Box>
        )
    }

    return (
        <Box sx={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
            <Container component='div' maxWidth='sm'>
                <Paper elevation={3} sx={{ borderRadius: 2 }}>
                    <Card>
                        <CardContent sx={{ p: 4 }}>
                            <Stack spacing={3} alignItems='center'>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <LockOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
                                </Box>
                                <Typography component="h1" variant="h5" sx={{ fontWeight: 600 }}>
                                    Sign In
                                </Typography>
                                <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
                                    <Stack spacing={2.5}>
                                        <TextField
                                            required
                                            fullWidth
                                            error={!!emailError}
                                            helperText={emailError}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onFocus={handleEmailFocus}
                                            onBlur={handleEmailBlur}
                                            label='Email'
                                            autoComplete='email'
                                            type='email'
                                            autoCapitalize='off'
                                            autoCorrect='off'
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            value={password}
                                            error={!!passwordError}
                                            helperText={passwordError}
                                            onFocus={handlePasswordFocus}
                                            onChange={(e) => setPassword(e.target.value)}
                                            type='password'
                                            label='Password'
                                            autoComplete='current-password'
                                        />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <FormControlLabel
                                                control={<Checkbox value={rememberMe} onChange={e => setRememberMe(e.target.checked)} />}
                                                label='Remember me'
                                            />
                                            <RouterLink to='/auth/forgot-password' style={{ textDecoration: 'none' }}>
                                                <Link component='span' sx={{ fontSize: '0.875rem' }}>Forgot password?</Link>
                                            </RouterLink>
                                        </Box>
                                        <Button
                                            disabled={submitting || !email || !password || !!emailError}
                                            type='submit'
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                        >
                                            Sign In
                                        </Button>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant='caption' color='text.secondary'>
                                                This site is protected by reCAPTCHA and the Google&nbsp;
                                                <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">Privacy Policy</Link> and&nbsp;
                                                <Link href="https://policies.google.com/terms" target="_blank" rel="noopener">Terms of Service</Link> apply.
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ my: 2 }}>
                                            <Typography variant='body2' color='text.secondary'>OR</Typography>
                                        </Divider>
                                        <Button
                                            variant='outlined'
                                            fullWidth
                                            size="large"
                                            onClick={handleGoogleSignIn}
                                            startIcon={
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                    <path fill="currentColor" d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032 s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2 C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"></path>
                                                </svg>
                                            }
                                        >
                                            Sign in with Google
                                        </Button>
                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            <Typography variant='body2' color='text.secondary'>
                                                Don't have an account?{' '}
                                                <RouterLink to='/auth/create-account' style={{ textDecoration: 'none' }}>
                                                    <Link component='span' sx={{ fontWeight: 600 }}>Sign up</Link>
                                                </RouterLink>
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Paper>
            </Container>
        </Box>
    )
}